import { FC, useEffect, useState } from "react";
import { Accordion, AccordionDetails, AccordionSummary, Box, Button, Checkbox, Typography, useTheme } from "@mui/material";
import { ExpandMoreRounded } from "@mui/icons-material";
import { format } from "date-fns";
import { PatientHexAvatar } from "../../../../components/ui";
import { HistoryConsultation, PatientGroup as PatientGroupModel } from "../history-filters";
import ConsultationActions from "./ConsultationActions";
import SelectionBar from "./SelectionBar";

// Quantas consultas revelar por vez: evita que um paciente com dezenas de
// consultas transforme o card numa lista gigante ao abrir.
const VISIBLE_STEP = 5;

type Props = {
    group: PatientGroupModel;
    defaultExpanded?: boolean;
    onOpen: (row: HistoryConsultation) => void;
    onPreview: (item: HistoryConsultation) => void;
    onRecipe: (item: HistoryConsultation) => void;
    onEdit: (item: HistoryConsultation) => void;
    onDuplicate: (item: HistoryConsultation) => void;
    onDelete: (item: HistoryConsultation) => void;
    /** Gera uma receita única com as consultas marcadas deste paciente. */
    onGenerateMany: (rows: HistoryConsultation[]) => void;
    /** true enquanto alguma receita está sendo gerada na tela. */
    generating?: boolean;
};

const PatientGroup: FC<Props> = ({
    group,
    defaultExpanded,
    onOpen,
    onPreview,
    onRecipe,
    onEdit,
    onDuplicate,
    onDelete,
    onGenerateMany,
    generating,
}) => {
    const theme = useTheme();
    const [expanded, setExpanded] = useState(!!defaultExpanded);
    const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
    const [visibleCount, setVisibleCount] = useState(VISIBLE_STEP);
    const count = group.consultations.length;

    // Filtro/página podem reaproveitar este card para outro paciente; a seleção
    // é sempre de um paciente só (é o que o backend aceita numa receita).
    useEffect(() => {
        setSelectedIds(new Set());
        setVisibleCount(VISIBLE_STEP);
    }, [group.patientId]);

    const toggle = (id: number) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) {
                next.delete(id);
            } else {
                next.add(id);
            }
            return next;
        });
    };

    const handleExpandedChange = (value: boolean) => {
        setExpanded(value);
        if (!value) {
            setSelectedIds(new Set());
            setVisibleCount(VISIBLE_STEP);
        }
    };

    // Mantém a ordem em que as consultas aparecem na tela, não a ordem dos cliques.
    const selectedRows = group.consultations.filter((row) => selectedIds.has(row.id));

    // group.consultations já vem ordenado da mais recente para a mais antiga,
    // então "as últimas N" são simplesmente as N primeiras.
    const visibleConsultations = group.consultations.slice(0, visibleCount);
    const remaining = count - visibleConsultations.length;

    return (
        <Accordion
            expanded={expanded}
            onChange={(_, value) => handleExpandedChange(value)}
            disableGutters
            elevation={0}
            sx={{
                borderRadius: "16px !important",
                border: `1px solid ${theme.palette.divider}`,
                backgroundColor: "background.paper",
                "&:before": { display: "none" },
                overflow: "hidden",
            }}
        >
            <AccordionSummary
                expandIcon={<ExpandMoreRounded sx={{ color: "text.secondary" }} />}
                sx={{ px: 2, py: 1, minHeight: 64, "& .MuiAccordionSummary-content": { alignItems: "center", gap: 1.5, my: 0 } }}
            >
                <PatientHexAvatar name={group.patientName} size={40} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography noWrap sx={{ fontSize: "14.5px", fontWeight: 600, color: "text.primary" }}>
                        {group.patientName}
                    </Typography>
                    <Typography noWrap sx={{ fontSize: "12px", color: "text.secondary", mt: 0.25 }}>
                        {count} {count === 1 ? "consulta" : "consultas"} · {format(new Date(group.lastDate), "dd/MM/yyyy")}
                    </Typography>
                </Box>
            </AccordionSummary>

            <AccordionDetails sx={{ p: 0, borderTop: `1px solid ${theme.palette.divider}` }}>
                {visibleConsultations.map((row) => (
                    <Box
                        key={row.id}
                        onClick={() => onOpen(row)}
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            px: 2,
                            py: 1.25,
                            cursor: "pointer",
                            borderBottom: `1px solid ${theme.palette.divider}`,
                            "&:last-of-type": { borderBottom: "none" },
                            "&:hover": { backgroundColor: theme.palette.brand.surface2 },
                        }}
                    >
                        {/* stopPropagation: marcar não pode abrir a consulta */}
                        <Box sx={{ flexShrink: 0 }} onClick={(e) => e.stopPropagation()}>
                            <Checkbox
                                size="small"
                                color="primary"
                                checked={selectedIds.has(row.id)}
                                onChange={() => toggle(row.id)}
                                inputProps={{ "aria-label": `Selecionar ${row.focus || (row.type === "ORAL" ? "Consulta oral" : "Consulta tópica")}` }}
                                sx={{ p: 0.5 }}
                            />
                        </Box>

                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography noWrap sx={{ fontSize: "13.5px", fontWeight: 500, color: "text.primary" }}>
                                {row.focus || (row.type === "ORAL" ? "Consulta oral" : "Consulta tópica")}
                            </Typography>
                            <Typography sx={{ fontSize: "11.5px", color: "text.secondary", mt: 0.25 }}>
                                {[row.type === "ORAL" ? "Oral" : "Tópica", format(new Date(row.date), "dd/MM/yyyy")].join(" · ")}
                            </Typography>
                        </Box>

                        <Box sx={{ flexShrink: 0 }} onClick={(e) => e.stopPropagation()}>
                            <ConsultationActions item={row} onPreview={onPreview} onRecipe={onRecipe} onEdit={onEdit} onDuplicate={onDuplicate} onDelete={onDelete} />
                        </Box>
                    </Box>
                ))}

                {remaining > 0 && (
                    <Button
                        fullWidth
                        variant="text"
                        onClick={() => setVisibleCount((prev) => prev + VISIBLE_STEP)}
                        sx={{
                            justifyContent: "flex-start",
                            gap: 0.5,
                            px: 2,
                            py: 1,
                            fontSize: "12.5px",
                            fontWeight: 500,
                            color: "text.secondary",
                            textTransform: "none",
                            borderRadius: 0,
                            borderTop: `1px solid ${theme.palette.divider}`,
                        }}
                    >
                        Ver mais {Math.min(VISIBLE_STEP, remaining)}{" "}
                        <Box component="span" sx={{ color: "text.disabled", fontWeight: 400 }}>
                            · {remaining} restantes
                        </Box>
                    </Button>
                )}

                {/* Sem position: sticky — o overflow: hidden do Accordion (cantos
                    arredondados) anula sticky em qualquer descendente. A lista
                    mostra 5 por vez justamente para a barra não ficar longe. */}
                <SelectionBar
                    count={selectedRows.length}
                    loading={generating}
                    onClear={() => setSelectedIds(new Set())}
                    onGenerate={() => onGenerateMany(selectedRows)}
                />
            </AccordionDetails>
        </Accordion>
    );
};

export default PatientGroup;
